/**
 * SF-4848: the links you need once a demo is made: take one of its surveys, sign in as the demo
 * user, open the demo company in the web app as yourself, and open it here. sf-api builds every
 * address; a link it does not send is not shown. Sign in as the demo user is hidden on a read-only
 * build, as the demo login is. When sf-api could not read the survey links, it says so with
 * Try again (which reads the build again) instead of showing no surveys.
 */
import { Link } from 'react-router-dom';
import { Button, Space, Typography } from 'antd';
import { WebAppLink } from '../../../components/WebAppLink';
import type { DemoBuildRunView } from '../api/demoBuildsClient';

export const QuickLinks: React.FC<{
  build: DemoBuildRunView;
  companyId: string;
  /** Someone else's build: no sign in as the demo user. */
  readOnly: boolean;
  /** Reads the build again, after sf-api could not read the survey links. */
  onRetry: () => void;
  /** The build is being read again. */
  retrying: boolean;
}> = ({ build, companyId, readOnly, onRetry, retrying }) => {
  const surveys = build.survey_links ?? [];
  const surveysFailed = build.survey_links_error === true;
  const signIn = !readOnly && build.demo_sign_in_link;

  return (
    <Space direction="vertical" size="middle" data-testid="demo-quick-links">
      {(surveys.length > 0 || surveysFailed) && (
        <Space direction="vertical" size={4} data-testid="demo-survey-links">
          <Typography.Text strong>Take a survey</Typography.Text>
          {surveys.map((survey) => (
            <a key={survey.id} href={survey.url} target="_blank" rel="noopener noreferrer">
              {survey.name || 'Untitled survey'}
            </a>
          ))}
          {surveysFailed && (
            <Space size={8} wrap>
              <Typography.Text type="danger">Could not load the survey links.</Typography.Text>
              <Button size="small" loading={retrying} onClick={onRetry}>
                Try again
              </Button>
            </Space>
          )}
        </Space>
      )}
      {signIn && (
        <Space direction="vertical" size={0}>
          <a href={signIn} target="_blank" rel="noopener noreferrer">
            Sign in as the demo user
          </a>
          <Typography.Text type="secondary">
            Right-click and choose Open link in incognito window, then copy the email and password
            from Demo login below. Opening it in this window signs you out of your own account in
            the web app.
          </Typography.Text>
        </Space>
      )}
      {build.web_app_link && <WebAppLink href={build.web_app_link} label="Open in web app as me" />}
      <Link
        to={`/companies/${encodeURIComponent(companyId)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open the demo company
      </Link>
    </Space>
  );
};
